import { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import API_URL from '../../config';

/**
 * Componente de identificação do imóvel.
 * Recebe onSelect(data) onde data = { codigo, nome, isNew } ou null.
 * initialCodigo: pré-preenche o campo e dispara verificação automaticamente (ex: seleção no DXF).
 */
function ImovelSelector({ onSelect, initialCodigo }) {
    const [codigo, setCodigo] = useState(initialCodigo || '');
    const [nome, setNome] = useState('');
    const [status, setStatus] = useState(null); // 'found' | 'new' | null
    const [imovelNomeExistente, setImovelNomeExistente] = useState('');
    const [checking, setChecking] = useState(false);
    const lastChecked = useRef('');

    const checkCodigo = async (value) => {
        const trimmed = value.trim();
        if (!trimmed || trimmed === lastChecked.current) return;
        lastChecked.current = trimmed;
        setChecking(true);
        try {
            const res = await axios.get(
                `${API_URL}/api/imoveis/${encodeURIComponent(trimmed)}`,
                { withCredentials: true }
            );
            setStatus('found');
            setImovelNomeExistente(res.data.nome || '');
            onSelect({ codigo: trimmed, nome: res.data.nome || '', isNew: false });
        } catch (err) {
            if (err.response?.status === 404) {
                setStatus('new');
                setImovelNomeExistente('');
                onSelect({ codigo: trimmed, nome: nome, isNew: true });
            }
        } finally {
            setChecking(false);
        }
    };

    // Quando montado com initialCodigo (vindo do DXF), dispara verificação automática
    useEffect(() => {
        if (initialCodigo) {
            checkCodigo(initialCodigo);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleCodigoChange = (e) => {
        setCodigo(e.target.value);
        setStatus(null);
        lastChecked.current = '';
        onSelect(null);
    };

    const handleCodigoBlur = () => checkCodigo(codigo);

    const handleCodigoKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            checkCodigo(codigo);
        }
    };

    const handleNomeChange = (e) => {
        setNome(e.target.value);
        if (status === 'new') {
            onSelect({ codigo: codigo.trim(), nome: e.target.value, isNew: true });
        }
    };

    return (
        <div className="bc-section" style={{ background: '#f0f4ff', borderLeft: '4px solid #4361ee' }}>
            <div className="bc-section-title">
                <span className="bc-section-num" style={{ background: '#4361ee' }}>ID</span>
                Identificação do Imóvel
            </div>

            <div className="mb-3">
                <label className="bc-label">Código do Imóvel</label>
                <div className="input-group">
                    <input
                        type="text"
                        className="form-control bc-input"
                        placeholder="Ex: CASA-001, IM2024-123..."
                        value={codigo}
                        onChange={handleCodigoChange}
                        onBlur={handleCodigoBlur}
                        onKeyDown={handleCodigoKeyDown}
                        maxLength={50}
                    />
                    {checking && (
                        <span className="input-group-text">
                            <span className="spinner-border spinner-border-sm" role="status" />
                        </span>
                    )}
                    {status === 'found' && (
                        <span className="input-group-text bg-success text-white border-success fw-bold">
                            Imóvel encontrado
                        </span>
                    )}
                    {status === 'new' && (
                        <span className="input-group-text bg-primary text-white border-primary fw-bold">
                            Novo imóvel
                        </span>
                    )}
                </div>
                <div className="bc-help">
                    {!status && 'Digite o código e pressione Tab ou Enter para verificar.'}
                    {status === 'found' && (
                        <span className="text-success">
                            Este cálculo será vinculado ao imóvel existente
                            {imovelNomeExistente ? ` "${imovelNomeExistente}"` : ''}.
                        </span>
                    )}
                    {status === 'new' && (
                        <span className="text-primary">
                            Código novo — um imóvel será criado ao salvar.
                        </span>
                    )}
                </div>
            </div>

            {status === 'new' && (
                <div className="mb-0">
                    <label className="bc-label">Nome do Imóvel <span className="text-muted fw-normal">(opcional)</span></label>
                    <input
                        type="text"
                        className="form-control bc-input"
                        placeholder="Ex: Casa da Rua das Flores, 123"
                        value={nome}
                        onChange={handleNomeChange}
                        maxLength={200}
                    />
                </div>
            )}
        </div>
    );
}

export default ImovelSelector;
